<template>
  <div class="search-container">
    <el-select
      v-model="searchCategory"
      placeholder="请选择"
      class="search-category"
      size="large"
      @change="handleCategoryChange"
    >
      <el-option label="请选择" value=""></el-option>
      <el-option label="名称" value="name"></el-option>
      <el-option label="作者" value="author"></el-option>
      <el-option label="馆藏位置" value="location"></el-option>
    </el-select>
    <el-input
      v-model="searchKeyword"
      placeholder="请输入搜索内容"
      class="search-input"
      size="large"
      @keyup.enter="handleSearch"
    >
      <el-button
        slot="append"
        icon="el-icon-search"
        @click="handleSearch"
        class="search-btn"
      >
        搜索
      </el-button>
    </el-input>
  </div>
</template>

<script>
export default {
  name: 'SearchBox',
  props: {
    // 是否显示为白色主题（用于深色背景）
    whiteTheme: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      searchCategory: '', // 显示"请选择"，但逻辑默认为name
      searchKeyword: ''
    }
  },
  
  mounted() {
    // 从路由参数初始化搜索框的值
    this.initFromRoute()
  },
  
  watch: {
    // 监听路由变化，更新搜索框的值
    '$route'() {
      this.initFromRoute()
    }
  },
  methods: {
    // 从路由参数初始化搜索框的值
    initFromRoute() {
      const query = this.$route.query
      
      console.log('🔍 SearchBox: initFromRoute 被调用', {
        'URL路径': this.$route.path,
        '查询参数': query,
        '当前搜索框值': this.searchKeyword
      })
      
      // 检查是否是纯分类页面（只有category参数，没有search参数）
      const isPureCategoryPage = query.category && !query.search && !query.search_type
      
      if (isPureCategoryPage) {
        // 如果是纯分类页面，清空搜索框
        this.searchKeyword = ''
        this.searchCategory = ''
        console.log('🔍 SearchBox: 检测到纯分类页面，清空搜索框')
      } else {
        // 设置搜索关键词
        if (query.search) {
          this.searchKeyword = query.search
          console.log('🔍 SearchBox: 从路由初始化搜索关键词:', query.search)
        } else {
          this.searchKeyword = ''
        }
        
        // 设置搜索类型
        if (query.search_type) {
          this.searchCategory = query.search_type
          console.log('🔍 SearchBox: 从路由初始化搜索类型:', query.search_type)
        } else {
          this.searchCategory = ''
        }
      }
    },
    
    handleSearch() {
      // 构建搜索参数
      const searchParams = {
        // 不指定category，默认搜索全部类别
        page: 1
      }
      
      // 如果有搜索内容，添加搜索参数
      if (this.searchKeyword.trim()) {
        searchParams.search = this.searchKeyword.trim()
        // 添加搜索类型参数（如果用户未选择，默认为name）
        searchParams.search_type = this.searchCategory || 'name'
      }
      
      // 调试信息
      console.log('🔍 搜索参数详情:', {
        '用户输入关键词': this.searchKeyword,
        '选择的搜索类型': this.searchCategory,
        '生成的URL参数': searchParams,
        '期望的API参数映射': {
          search: searchParams.search,
          search_type: searchParams.search_type
        }
      })
      
      // 检查是否为相同的搜索（避免重复搜索）
      const currentQuery = this.$route.query
      const isSameSearch = (
        currentQuery.search === searchParams.search &&
        currentQuery.search_type === searchParams.search_type &&
        !currentQuery.category && // 确保当前也没有指定类别（即全部类别）
        this.$route.path === '/products'
      )
      
      if (isSameSearch) {
        return
      }
      
      // 跳转到产品列表页面并执行搜索
      // 如果当前已在产品页面，使用replace避免重复历史记录
      const currentPath = this.$route.path
      const navigationMethod = currentPath === '/products' ? 'replace' : 'push'
      
      this.$router[navigationMethod]({
        path: '/products',
        query: searchParams
      }).catch(err => {
        // 忽略重复导航错误
        if (err.name !== 'NavigationDuplicated') {
          console.error('导航错误:', err)
        }
      })
      
      // 同时触发原有的事件，保持兼容性
      this.$emit('search', {
        category: this.searchCategory,
        keyword: this.searchKeyword
      })
    },
    
    handleCategoryChange() {
      this.$emit('category-change', this.searchCategory)
    }
  }
}
</script>

<style lang="scss" scoped>
.search-container {
  display: flex;
  max-width: 900px;
  margin: 0 auto;
  gap: 0; // 保持紧密连接

  @media (max-width: 768px) {
    max-width: 95%;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    padding: 0 5px;
  }

  .search-category {
    width: 150px;
    flex-shrink: 0; // 防止压缩

    @media (max-width: 768px) {
      width: 120px;
    }

    @media (max-width: 480px) {
      width: 100px;
    }
  }

  .search-input {
    flex: 1;
    min-width: 0; // 允许收缩
  }

  :deep(.el-select .el-input__inner) {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid var(--border-color);
    border-radius: 20px 0 0 20px;
    height: 40px;

    @media (max-width: 768px) {
      border-radius: 20px 0 0 20px; // 保持左侧圆角
      height: 38px;
    }

    &:focus {
      border-color: var(--border-color);
      box-shadow: none;
      outline: none;
    }

    &:hover {
      border-color: var(--border-color);
    }
  }

  // 主输入框样式（带按钮的输入框）
  :deep(.el-input-group .el-input__inner) {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid var(--border-color);
    border-radius: 0;
    height: 40px;

    @media (max-width: 768px) {
      border-radius: 0; // 保持中间无圆角
      height: 38px;
    }

    &:focus {
      border-color: var(--border-color);
      box-shadow: none;
      outline: none;
    }

    &:hover {
      border-color: var(--border-color);
    }
  }

  // 输入框组的包装样式
  :deep(.el-input-group--append .el-input__wrapper) {
    border-radius: 0;
  }

  // 按钮组样式
  :deep(.el-input-group__append) {
    border-radius: 0 20px 20px 0;
    background: none;
    border: none;
    padding: 0;

    @media (max-width: 768px) {
      border-radius: 0 20px 20px 0; // 保持右侧圆角
    }
  }

  // 搜索按钮样式
  :deep(.el-button) {
    background: #000000;
    border-color: #000000;
    color: #ffffff;
    border-radius: 0 20px 20px 0;
    height: 40px;
    margin: 0;

    @media (max-width: 768px) {
      border-radius: 0 20px 20px 0; // 保持右侧圆角
      height: 38px;
    }

    &:hover {
      background: #333333;
      border-color: #333333;
      color: #ffffff;
    }

    &:focus {
      background: #000000;
      border-color: #000000;
      color: #ffffff;
      box-shadow: none;
      outline: none;
    }
  }

  // 移除Element UI组件的默认焦点效果
  :deep(.el-select) {
    &:focus-within {
      box-shadow: none;
    }

    .el-input__inner {
      &:focus {
        box-shadow: none !important;
        outline: none !important;
      }
    }
  }

  :deep(.el-input) {
    &:focus-within {
      box-shadow: none;
    }

    .el-input__inner {
      &:focus {
        box-shadow: none !important;
        outline: none !important;
      }
    }
  }
}

// 深色背景主题样式（用于产品列表页）
.search-container.dark-theme {
  :deep(.el-select .el-input__inner),
  :deep(.el-input-group .el-input__inner) {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(255, 255, 255, 0.3);
    
    &:focus,
    &:hover {
      border-color: rgba(255, 255, 255, 0.5);
    }
  }
  
  :deep(.el-button) {
    background: rgba(0, 0, 0, 0.8);
    border-color: rgba(0, 0, 0, 0.8);
    
    &:hover {
      background: rgba(0, 0, 0, 0.9);
      border-color: rgba(0, 0, 0, 0.9);
    }
  }
}
</style>
